<template>
  <v-form
    @submit.prevent="maybeSignup"
    v-model="isFormValid"
    :disabled="loading"
  >
    <v-text-field
      v-model="first_name"
      color="deep-orange"
      label="First Name"
      name="First Name"
      large
      :rules="[ValidationRules.requiredString]"
      autofocus
    />
    <v-text-field
      v-model="last_name"
      color="deep-orange"
      label="Last Name"
      name="Last Name"
      large
    />
    <v-text-field
      v-model="email"
      class="email-input"
      color="deep-orange"
      placeholder="Email"
      label="Email"
      name="Email"
      large
      :rules="[ValidationRules.requiredString, ValidationRules.emailRule]"
      :error-messages="emailError"
      @input="resetError('email')"
    />
    <v-text-field
      v-model="phone"
      class="phone-input"
      color="deep-orange"
      placeholder="Phone"
      label="Phone"
      name="Phone"
      large
      :rules="phoneValidationRules"
      :error-messages="phoneError"
      @input="resetError(['phone', 'phone_normalized'])"
    />
    <template v-if="!getShouldHideAddressFields">
      <v-text-field
        v-model="street"
        class="street-input"
        color="deep-orange"
        label="Street Address"
        name="Street Address"
        large
      />
      <v-text-field
        v-model="zip"
        class="zip-input"
        color="deep-orange"
        label="Zipcode"
        name="Zipcode"
        large
        :rules="[zipRule]"
        :error-messages="errors.zip"
        @input="resetError('zip')"
      />
    </template>

    <template v-if="getShouldCapturePaymentInfo && getPaymentOptions.length">
      <v-select
        v-model="payment_service"
        :items="getPaymentOptions"
        color="deep-orange"
        label="Payment Method"
        large
        clearable
        :rules="[paymentServiceRule]"
      />
      <v-text-field
        v-if="!isCashOrCreditSelected"
        v-model="payment_handle"
        color="deep-orange"
        label="Payment Handle (e.g. @minimartapp)"
        large
        :rules="[paymentHandleRule]"
      />
    </template>

    <SetPasswordField @input="password = $event" />
    <PasswordField
      v-model="password_confirmation"
      data-qa="password-confirmation"
      :rules="[ValidationRules.requiredString, matchPasswordRule]"
      label="Re-enter Your Password"
    />
    <div class="mt-2 mb-4 mx-3"
      >By signing up, you hereby consent to receiving a single text message for
      the purpose of phone number verification.</div
    >
    <v-btn
      color="amber darken-3"
      :disabled="!isFormValid"
      class="white--text"
      :loading="loading"
      depressed
      x-large
      block
      data-qa="customer-signup-submit"
      type="submit"
      >Create Account</v-btn
    >
    <transition name="fade" mode="out-in">
      <v-alert
        v-if="phoneUniqueError"
        class="my-3 px-8"
        color="blue darken-2"
        text
        outlined
      >
        <p class="mb-2 title"
          >This phone number is already in-use in this or another Minimart shop.
          Click below to login with this number or use a diffent one to
          signup:</p
        >
        <BtnBaseBlue
          :to="{ name: LoginPhoneCheckRoute }"
          color="blue darken-2"
          depressed
          exact
          class="mb-2"
          >Phone Number Login</BtnBaseBlue
        >
      </v-alert>
    </transition>
    <transition name="fade" mode="out-in">
      <v-alert
        v-if="genericError"
        class="my-3 px-8"
        color="blue darken-2"
        text
        outlined
      >
        <p class="mb-2 title">{{ genericError }}</p>
      </v-alert>
    </transition>
  </v-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { CustomerRoutes } from '@/constants/RouteNames'
import ValidationRules from '@/constants/ValidationRules'
import { CREDIT_CARD, CASH } from '@/constants/PaymentProviders'
import SetPasswordField from '@/components/SetPasswordField'
import PasswordField from '@/components/PasswordField'
import BtnBaseBlue from '@/components/BtnBaseBlue'
import _get from 'lodash/get'

export default {
  name: 'SignupEmailPassForm',
  components: { SetPasswordField, PasswordField, BtnBaseBlue },
  data() {
    return {
      isFormValid: false,
      loading: false,
      errors: false,
      phone: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      street: '',
      zip: '',
      password_confirmation: '',
      payment_service: '',
      payment_handle: '',
      ValidationRules,
      LoginPhoneCheckRoute: CustomerRoutes.LOGIN_WITH_PHONE,
      matchPasswordRule: (v) =>
        v === this.password || `The two fields must match.`,
    }
  },
  created() {
    if (this.getCustomerEmail) {
      this.email = this.getCustomerEmail
    }
  },
  computed: {
    ...mapGetters('customer', ['getCustomerEmail']),
    ...mapGetters('shop', [
      'getShouldHideAddressFields',
      'getIsPhoneRequiredForSignup',
      'getShouldCapturePaymentInfo',
      'getPaymentOptions',
    ]),
    zipRule() {
      // Only require if they entered a street
      if (!this.street) {
        return () => true
      }
      return ValidationRules.zipRule
    },
    phoneValidationRules() {
      let rules = [ValidationRules.phoneRule]
      if (this.getIsPhoneRequiredForSignup) {
        rules.push(ValidationRules.requiredString)
      }
      return rules
    },
    paymentServiceRule() {
      if (!this.payment_handle) {
        return () => true
      }
      return (v) => !!v || 'Please select an option'
    },

    paymentHandleRule() {
      return this.isCashOrCreditSelected
        ? () => true
        : ValidationRules.requiredString
    }, // paymentHandleRule

    isCashOrCreditSelected() {
      return [CASH, CREDIT_CARD].includes(this.payment_service)
    }, // isCashOrCreditSelected
    phoneError() {
      const uniqueErrorMsg = `This number is already registered at this or another Minimart shop.`
      return this.phoneUniqueError ? [uniqueErrorMsg] : this.errors.phone
    }, // phoneError
    phoneUniqueError() {
      return (
        _get(this.errors, 'phone', []).includes('validation.unique') ||
        _get(this.errors, 'phone_normalized', []).includes('validation.unique')
      )
    },
    emailError() {
      const uniqueErrorMsg = `This email is already registered. Try logging in or choose a different email.`
      return this.emailUniqueError ? [uniqueErrorMsg] : this.errors.email
    }, // emailError
    emailUniqueError() {
      return _get(this.errors, 'email[0]') === 'validation.unique'
    },
    genericError() {
      return _get(this.errors, 'generic')
    },
  },
  methods: {
    ...mapActions('auth', ['customerSignup', 'customerLogin']),

    async maybeSignup() {
      this.loading = true
      this.errors = false
      try {
        let customer = await this.customerSignup({
          first_name: this.first_name,
          last_name: this.last_name,
          email: this.email,
          phone: this.phone,
          password: this.password,
          password_confirmation: this.password_confirmation,
          payment_service: this.payment_service,
          payment_handle: this.payment_handle,
        })
        if (customer) {
          await this.customerLogin({
            email: this.email,
            password: this.password,
          })
          let route = this.$route.query.next
            ? this.$route.query.next
            : CustomerRoutes.ORDERS_INDEX

          this.$router.push({
            name: route,
            query: { ...this.$route.query, welcome: true },
          })
        } else {
          this.errors = {
            generic:
              "Sorry, we couldn't create an account with those details. Please try again.",
          }
        }
      } catch (error) {
        const errorBag = _get(error, 'response.data.errors')
        if (errorBag) {
          this.errors = errorBag
        } else {
          this.errors = {
            generic:
              "Sorry, we couldn't create an account with those details. Please try again.",
          }
        }
      } finally {
        this.loading = false
      }
    }, // maybeSignup
    resetError(keyOrKeys) {
      let keys = !Array.isArray(keyOrKeys) ? [keyOrKeys] : keyOrKeys
      keys.forEach((key) => {
        if (!_get(this, `errors[${key}]`)) return
        this.errors[key] = []
      })
    },
  }, // methods
  watch: {
    isCashOrCreditSelected(val) {
      if (val) {
        this.payment_handle = ''
      }
    },
  },
}
</script>
